import FiscalYearTableCell from "components/company/financials/american/FiscalYearTableCell";
import type { IAmericanCompanyIssuerFinancialsFieldsFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { ReactElement } from "react";
import FinancialValueThresholdColumnStyles from "../../../financials/FinancialValueThresholdColumnStyles";
import * as palette from "../../../palette";
import TextTooltip from "../../../shared/TextTooltip";
import formatFinancialValue from "../formatFinancialValue";

interface IBankFinancialsProps {
  onFilterByQuarter: (reportDate: Date) => void;
  displayAllColumns: boolean;
  financials:
    | IAmericanCompanyIssuerFinancialsFieldsFragment["yearly"]
    | IAmericanCompanyIssuerFinancialsFieldsFragment["quarterly"];
}

export default function BankFinancials({
  onFilterByQuarter,
  displayAllColumns,
  financials,
}: IBankFinancialsProps): ReactElement | null {
  if (financials.length === 0) {
    return null;
  }

  const isQuarterly = financials.every((row) => row.statementType === "QUARTERLY");
  const hasRepurchase = financials.some((financialStatementRow) => {
    return financialStatementRow.cashFlow.repurchaseOfStock !== 0;
  });
  const hasPayout = isQuarterly
    ? false
    : financials.some((financials) => {
        return financials.ratios.dividendPayoutRatio > 0;
      });

  const hasDividendYield = financials.some((financials) => {
    return financials.ratios.dividendYield > 0;
  });

  return (
    <table
      css={{
        borderRadius: 4,
        borderSpacing: 0,
        borderCollapse: "collapse",
        "> thead > tr > th:first-of-type": {
          borderTopLeftRadius: 4,
        },
        "> thead > tr > th:last-of-type": {
          borderTopRightRadius: 4,
        },
        "> tbody > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 4,
        },
        "> tbody > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 4,
        },
        "> thead > tr > th": {
          position: "sticky",
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
      }}
    >
      <thead>
        <tr>
          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.white,
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            Year
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            Total Assets
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalEquity" placement="top" content="Assets - Liabilities">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Equity
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/revenue" placement="top" content="Income from Products and Services">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Revenue
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netIncome" placement="top" content="Revenue - Discounts">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Net Income
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netMargin" placement="top" content="Net Income / Revenue">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Net Mar.
                </span>
              )}
            </TextTooltip>
          </th>

          {!isQuarterly && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/returnOnEquity" placement="top" content="Return on Equity">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    ROE
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/provisionForCreditLosses"
              placement="top"
              content="Provision for Credit Losses"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  PCL
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            PCL/Net Income
          </th>

          {hasRepurchase && displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.luxorGold,
                fontWeight: 600,
                backgroundColor: palette.linen,
                borderLeft: `solid 1px ${palette.chamois}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/repurchaseOfStock" placement="top" content="Repurchase Of Capital Stock">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Buyback
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/cashFlowForDividends" placement="top" content="Cash Flow for Dividends">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Dividends
                </span>
              )}
            </TextTooltip>
          </th>

          {hasPayout && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.luxorGold,
                backgroundColor: palette.linen,
                borderLeft: `solid 1px ${palette.chamois}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/cashFlowForDividendsToNetIncomeRatio"
                placement="top"
                content="Cash Flow for Dividends / Net Income"
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Payout
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          {hasDividendYield && displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.luxorGold,
                backgroundColor: palette.linen,
                borderLeft: `solid 1px ${palette.chamois}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/dividendYield" placement="top" content="Dividend Yield">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    DY
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.mineShaft,
                fontWeight: 600,
                backgroundColor: palette.catskillWhite,
                borderLeft: `solid 1px ${palette.mystic}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/earningsPerShareDiluted" placement="top" content="Earnings Per Share">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    EPS
                  </span>
                )}
              </TextTooltip>
            </th>
          )}
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderLeft: `solid 1px ${palette.mystic}`,
            borderRight: `solid 1px ${palette.mystic}`,
            borderBottom: `solid 1px ${palette.mystic}`,
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {financials.map((financialStatementRow) => {
          return (
            <tr key={financialStatementRow.fiscalYear as string}>
              {false && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    backgroundColor: palette.blueBayoux,
                    fontWeight: 600,
                    color: palette.white,
                    lineHeight: "20px",
                    borderTop: `solid 1px ${palette.lynch}`,
                    borderBottom: `solid 1px ${palette.lynch}`,
                    borderRight: `solid 1px ${palette.lynch}`,
                  }}
                >
                  {financialStatementRow.fiscalYear}
                </td>
              )}

              <FiscalYearTableCell
                row={financialStatementRow}
                onFilterByQuarter={(reportDate) => onFilterByQuarter(reportDate)}
              />

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.balanceSheet.totalAssets < 0 ? palette.mojo : palette.seaGreen,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.balanceSheet.totalAssets)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.balanceSheet.totalEquity < 0 ? palette.mojo : palette.punga,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.balanceSheet.totalEquity)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.fruitSalad,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.revenue)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.incomeStatement.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                  border: `solid 1px ${palette.fringyFlower}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.netIncome)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  color: financialStatementRow.incomeStatement.netIncome < 0 ? palette.mojo : palette.blueBayoux,
                  fontWeight: financialStatementRow.incomeStatement.netIncome < 0 ? 600 : 500,
                  lineHeight: "20px",
                  borderLeft: `solid 1px ${palette.mystic}`,
                  border: `solid 1px ${palette.mystic}`,
                  backgroundColor: "#fafafa",
                }}
              >
                {financialStatementRow.incomeStatement.netIncome < 0
                  ? "L"
                  : `${financialStatementRow.incomeStatement.netMargin
                      .toFixed(
                        financialStatementRow.incomeStatement.netMargin < 1 &&
                          financialStatementRow.incomeStatement.netMargin !== 0
                          ? 1
                          : 0,
                      )
                      .replace(".", ",")}%`}
              </td>

              {!isQuarterly && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight:
                      financialStatementRow.balanceSheet.returnOnEquity < 0 ||
                      financialStatementRow.incomeStatement.netIncome < 0 ||
                      financialStatementRow.balanceSheet.totalEquity < 0
                        ? 600
                        : 500,
                    color:
                      financialStatementRow.balanceSheet.returnOnEquity < 0 ||
                      financialStatementRow.incomeStatement.netIncome < 0 ||
                      financialStatementRow.balanceSheet.totalEquity < 0
                        ? palette.mojo
                        : palette.blueBayoux,
                    lineHeight: "20px",
                    borderLeft: `solid 1px ${palette.mystic}`,
                    border: `solid 1px ${palette.mystic}`,
                    borderRight: "none",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {financialStatementRow.balanceSheet.returnOnEquity < 0 ||
                  financialStatementRow.incomeStatement.netIncome < 0 ||
                  financialStatementRow.balanceSheet.totalEquity < 0
                    ? "L"
                    : `${financialStatementRow.balanceSheet.returnOnEquity
                        .toFixed(financialStatementRow.balanceSheet.returnOnEquity < 1 ? 1 : 0)
                        .replace(".", ",")}%`}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color:
                    financialStatementRow.incomeStatement.creditLossesProvision <= 100000
                      ? palette.mojo
                      : palette.seaGreen,
                  border: `solid 1px #edd1d1`,
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.creditLossesProvision)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={Math.round(financialStatementRow.incomeStatement.creditLossesProvisionToNetIncomeRatio * 100)}
                comparators={[
                  (value) => {
                    return value > 0 && value < 75;
                  },
                  (value) => {
                    return value >= 75 && value < 100;
                  },
                  (value) => {
                    return value >= 100;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                ]}
              >
                {(creditLossesProvisionToNetIncomeRatio, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      color: palette.mojo,
                      border: `solid 1px #edd1d1`,
                      ...css,
                    }}
                  >
                    {(creditLossesProvisionToNetIncomeRatio || 0) < 0
                      ? "L"
                      : `${creditLossesProvisionToNetIncomeRatio}%`}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              {hasRepurchase && displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 500,
                    color: palette.luxorGold,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.chamois}`,
                    borderRight: "none",
                  }}
                >
                  {formatFinancialValue(financialStatementRow.cashFlow.repurchaseOfStock)}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: palette.luxorGold,
                  border: `solid 1px ${palette.chamois}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(financialStatementRow.cashFlow.cashFlowForDividends)}
              </td>

              {hasPayout && (
                <FinancialValueThresholdColumnStyles
                  value={Math.round(financialStatementRow.ratios.dividendPayoutRatio * 100)}
                  comparators={[
                    (value) => {
                      return value > 0 && value < 30;
                    },
                    (value) => {
                      return value >= 30 && value < 70;
                    },
                    (value) => {
                      return value >= 70;
                    },
                  ]}
                  styles={[
                    () => ({
                      color: palette.mojo,
                      backgroundColor: "#FCF1F1",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.kumera,
                      backgroundColor: palette.butteryWhite,
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.seaGreen,
                      backgroundColor: "#F1FFF3",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                  ]}
                >
                  {(dividendPayoutRatio, css) => (
                    <td
                      css={{
                        fontFamily: "novel-mono-pro-condensed",
                        fontWeight: 500,
                        color: palette.luxorGold,
                        border: `solid 1px ${palette.chamois}`,
                        borderRight: "none",
                        ...css,
                      }}
                    >
                      {dividendPayoutRatio}%
                    </td>
                  )}
                </FinancialValueThresholdColumnStyles>
              )}

              {hasDividendYield && displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 500,
                    color: palette.luxorGold,
                    border: `solid 1px ${palette.chamois}`,
                    borderRight: "none",
                  }}
                >
                  {`${financialStatementRow.ratios.dividendYield
                    .toFixed(financialStatementRow.ratios.dividendYield !== 0 ? 1 : 0)
                    .replace(".", ",")}%`}
                </td>
              )}

              {displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 600,
                    color: financialStatementRow.incomeStatement.epsDiluted < 0 ? palette.mojo : palette.blueBayoux,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.mystic}`,
                  }}
                >
                  {`${
                    financialStatementRow.incomeStatement.epsDiluted < 0
                      ? "L"
                      : financialStatementRow.incomeStatement.epsDiluted
                          .toFixed(financialStatementRow.incomeStatement.epsDiluted !== 0 ? 2 : 0)
                          .replace(".", ",")
                  }`}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
